<style>
.sticker-disabled {
  opacity: .1;
}
</style>

<template>
  <v-container fluid class="my-0 py-0">
    <v-window
      v-model="prepTaskWindow"
    >
      <v-window-item
        transition="none"
      >
        <template>
          <v-toolbar flat class="pb-10" :dense="denseView">
            <DebouncedTextField
              class="d-print-none"
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
              clearable
            />
            <v-spacer/>
            <v-btn-toggle
              v-model="search"
              dense
              rounded
            >
              <v-btn :value="null">All</v-btn>
              <v-btn
                v-for="team of teamFilter"
                v-bind:key="team"
                :value="`team:${team}`"
              >{{ team }}
              </v-btn>

            </v-btn-toggle>

            <v-spacer/>
            <ProductionSelector
              :dates="dates"
              :production="production"
              v-on:update:dates="dates=$event"
              v-on:update:production="production=$event"
            />
            <v-btn icon @click="showTimeoutDialog = true" class="ml-2">
              <v-icon>mdi-cog</v-icon>
            </v-btn>

          </v-toolbar>
          <v-toolbar flat v-if="!hideGarnish" :dense="denseView" class="ma-0 pa-0">
            <v-checkbox
              v-if="!alwaysShowGarnish"
              label="Show Garnish"
              v-model="showGarnish"
              dense
              hide-details
            />
            <v-checkbox
              class="pl-4"
              label="Only Garnish"
              v-model="onlyGarnish"
              dense
              hide-details
            />
            <v-spacer/>
            <template v-if="totalTimeEstimate">
              <v-chip outlined>est. {{ formatTimeDuration(totalTimeEstimate) }} ({{ tasksWithTimeEstimate.length }}
                tasks)
              </v-chip>
              <v-spacer/>
            </template>
            <v-checkbox
              class="pl-4"
              label="Dense View"
              v-model="denseView"
              dense
              hide-details
            />
          </v-toolbar>
          <TaskPanel
            show-team-member-buttons
            :one-task-at-a-time="oneTaskAtATime"
            :check-running-tasks="oneTaskAtATime"
            v-if="hasDates"
            :team="team"
            :dates="dates"
            :production="production"
            :search="search"
            :filter="filter"
            :show-late-warnings="showLateWarnings"
            :late-warning-priority-deadlines="lateWarningPriorityDeadlines"
            :sort-fn="sortFn"
            v-on:print-stickers="printStickers($event)"
            :dense-view="denseView"
            :filter-fn="taskFilter"
            v-on:update:tasks="tasks = $event"
          >
            <template v-slot:task-title="{ props: { task}}">
              <v-card-title>
                <v-row style="word-break: keep-all">
                  <v-col
                    align-self="center"
                    :style="denseView ? 'text-wrap: nowrap; overflow: clip; text-overflow: ellipsis':''"
                  >
                    <template v-if="denseView">
                      <v-btn icon class="green" v-if="(!task.isStarted && !task.isPaused) || task.complete">
                        <v-icon>mdi-play</v-icon>
                      </v-btn>
                      <v-btn icon class="yellow" v-if="!task.isStarted && task.isPaused && !task.complete">
                        <v-icon>mdi-pause</v-icon>
                      </v-btn>
                      <v-btn icon class="red" v-if="task.isStarted">
                        <v-icon>mdi-stop</v-icon>
                      </v-btn>
                    </template>
                    <span class="pl-1 font-weight-bold" v-if="task.source && task.source.batch">
                      [{{ task.source.batch }}]
                    </span>
                    <span class="ml-2">{{ removeBatch(task.title) }}</span>
                  </v-col>
                  <v-col cols="3" class="text-right">
                    <div v-html="formatTaskAmount(task)"></div>
                    <br v-if="!denseView"/>
                    <v-icon color="black" v-if="task.complete">mdi-check</v-icon>
                  </v-col>
                </v-row>
              </v-card-title>
              <v-card-text v-if="task.source && task.source.count">
                <v-chip outlined v-for="[size,amount] of Object.entries(task.source.count).filter(e => !!e[1])"
                        v-bind:key="`${task.id}-${amount}-${size}`">
                  {{ size }}: {{ amount }}
                </v-chip>
              </v-card-text>
              <v-card-text v-if="showAllergyInfo && getTaskAllergyRows(task).length" class="pt-0">
                <div v-for="row of getTaskAllergyRows(task)" v-bind:key="`${task.id}-${row.key}`" class="mb-1">
                  <span class="font-weight-bold mr-1">{{ row.label }}</span>
                  <v-chip v-for="name of row.names" v-bind:key="`${task.id}-${row.key}-${name}`"
                          small outlined color="red" class="mr-1">
                    {{ name }}
                  </v-chip>
                  <template v-if="row.notInComponent && row.notInComponent.length">
                    <span class="grey--text mr-1">not in component:</span>
                    <v-chip v-for="name of row.notInComponent" v-bind:key="`${task.id}-${row.key}-ni-${name}`"
                            small outlined color="grey" class="mr-1">
                      {{ name }}
                    </v-chip>
                  </template>
                </div>
              </v-card-text>
            </template>
            <!--      /* eslint-disable-next-line vue/no-unused-vars */-->
            <template v-slot:task-text="{ props: {task}}">
              <div class="d-flex justify-space-between align-center pb-2 mx-5">
                <div v-if="currentTeamWorkingOnTask(task).length > 0" class="text-left">
                  <v-chip-group>
                    <v-chip v-for="teamMember of currentTeamWorkingOnTask(task)" v-bind:key="teamMember" small>
                      {{ teamMember }}
                    </v-chip>
                  </v-chip-group>
                </div>
                <div class="text-right">
                  <div v-if="task.source.timeEstimate">
                    Estimate: {{ Math.round(task.source.timeEstimate) }} min
                  </div>
                  <div v-if="task.elapsed > 0">
                    Actual: {{ formatMinutesSeconds(task.elapsed) }}
                  </div>
                  <ul v-if="task.execution">
                    <li v-for="(e) of task.execution" v-bind:key="`${task.id}-${e.start}`">
                      {{ formatTaskExecution(e) }}
                    </li>
                  </ul>
                </div>
              </div>
            </template>
            <template v-slot:task-confirm-extra="{ props: { confirmTask } }">
              <v-card-actions v-if="!showUpdateEstimate">
                <span v-if="canSetEstimate || confirmTask.source.timeEstimate">Estimate: {{
                    confirmTask.source.timeEstimate || '?'
                  }} minutes to process {{ formatWeightWithUnits(confirmTask.source.rawTotal) }}</span>
                <v-spacer/>
                <v-btn v-if="canSetEstimate" @click="openTaskAnalysis(confirmTask)">Change Estimate</v-btn>
              </v-card-actions>
              <v-card-actions v-if="showUpdateEstimate">
                Time Estimate
                <v-text-field
                  class="ml-2"
                  :label="`minutes to process ${confirmTask.source.rawTotal ? formatWeightWithUnits(confirmTask.source.rawTotal):''}`"
                  v-model="updateTimeEstimate"
                  type="number"
                />
                <v-btn class="ml-2" @click="saveUpdatedEstimate(confirmTask, updateTimeEstimate)">Save</v-btn>
                <v-spacer/>
                <v-btn @click="showUpdateEstimate=false">Cancel</v-btn>
              </v-card-actions>
            </template>
          </TaskPanel>
          <v-alert v-if="!hasDates">
            Please select a production date
          </v-alert>
        </template>
      </v-window-item>
      <v-window-item transition="none">
        <template v-if="showStickers">
          <v-toolbar class="d-print-none" flat>
            <v-btn :loading="isPrinting" @click="closeStickers">Close</v-btn>
            <template v-if="hasPrint">
              <v-spacer/>
              <!--              <v-btn x-large @click="doPrint">Print All</v-btn>-->
              <v-spacer/>
              <v-btn :loading="isPrinting" @click="closeStickers">Close</v-btn>
            </template>
          </v-toolbar>
          <v-row dense v-for="(count,mealId) of printTask.source.counts" v-bind:key="mealId" class="">
            <v-col cols="12" class="d-print-none text-center" align-self="center">
              <h2>Rack {{ count.rack }} - {{ count.key }}</h2>
            </v-col>
            <template v-if="printTask.source.counts[mealId].totalAmountMain">
              <v-col :class="stickerClass[`${mealId}-main`]">
                <v-row class="d-print-none">
                  <v-col cols="" class=" text-center" align-self="center">
                    <v-btn x-large color="primary" :loading="isPrinting" @click="printOnly(mealId,'main')">Print Main
                    </v-btn>
                  </v-col>
                </v-row>
                <v-row dense>
                  <v-col class="text-left ">
                    <ComponentLabel
                      class=""
                      :production="production"
                      :rack="getMainRack(count)"
                      :title="`${count.key}`"
                      :code="`${mealId}:${production}`"
                      :text="`${printTask.source.component.name}`"
                      :production_priority="printTask.source.production_priority"
                      :batch="printTask.source.batch"
                      :amount="printTask.source.counts[mealId].totalAmountMain || 0"
                      :date="formatDateRange(dateFrom,dateTo)"
                      :portion-size="count.portionSize"
                      :portion-count="count.count"
                      :component="printTask.source.component"
                      :priority="printTask.source.production_priority?`P${printTask.source.production_priority}`:''"

                    />
                  </v-col>
                </v-row>
              </v-col>
            </template>
            <template v-if="printTask.source.counts[mealId].totalAmountAllergy">
              <v-col :class="stickerClass[`${mealId}-allergy`]">
                <v-row>
                  <v-col cols="" class="d-print-none text-center" align-self="center">
                    <v-btn x-large color="secondary" :loading="isPrinting" @click="printOnly(mealId,'allergy')">Print
                      Allergy
                    </v-btn>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <ComponentLabel
                      :rack="count.rack?`A${count.rack}`:''"
                      :production="production"
                      :title="`${count.key}`"
                      :code="`${mealId}:${production}`"
                      :text="`${printTask.source.component.name}`"
                      :production_priority="printTask.source.production_priority"
                      :batch="printTask.source.batch"
                      :amount="printTask.source.counts[mealId].totalAmountAllergy"
                      line="AL"
                      :date="formatDateRange(dateFrom,dateTo)"
                      :portion-size="count.portionSize"
                      :portion-count="count.allergyCount"
                      :component="printTask.source.component"
                      :priority="printTask.source.production_priority?`P${printTask.source.production_priority}`:''"

                    />
                  </v-col>
                </v-row>
              </v-col>

            </template>
            <template v-if="printTask.source.counts[mealId].totalAmountSub">
              <v-col :class="stickerClass[`${mealId}-sub`]">
                <v-row>
                  <v-col class="d-print-none text-center" align-self="center">
                    <v-btn x-large color="warning" :loading="isPrinting" @click="printOnly(mealId,'sub')">Print Sub
                    </v-btn>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <ComponentLabel
                      :rack="count.rack?`A${count.rack}`:''"
                      :production="production"
                      :title="`${count.key}`"
                      :code="`${mealId}:${production}`"
                      :text="`SUB for ${printTask.source.component.name}${formatAllergyNames(count.activeAllergy)}`"
                      :production_priority="printTask.source.production_priority"
                      :batch="printTask.source.batch"
                      :amount="printTask.source.counts[mealId].totalAmountSub"
                      line="S"
                      line-shape="square"
                      :date="formatDateRange(dateFrom,dateTo)"
                      :portion-size="count.portionSize"
                      :portion-count="count.substituteCount"
                      :component="printTask.source.component"
                      :priority="printTask.source.production_priority?`P${printTask.source.production_priority}`:''"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </template>
          </v-row>
        </template>
      </v-window-item>
    </v-window>
    <v-dialog v-model="showTimeoutDialog" max-width="400">
      <v-card>
        <v-card-title>Printing Options</v-card-title>
        <v-card-text>
          <v-text-field
            v-model.number="printTimeoutValue"
            label="Delay (seconds)"
            type="number"
            step="0.5"
            min="0.5"
            hint="Time to wait before and after printing (default: 1)"
            persistent-hint
          />
          <v-alert type="info" text class="mt-4">
            On some print stations a longer delay is needed.
            <br/>
            If more than one label is being printed then increasing the delay time can help.
            Note that increasing it will also slow down printing.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="showTimeoutDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="saveTimeout">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import {dateFormatted, extractFoodId, formatDuration, formatMinutesSeconds, formatWeight, getDatesInRange} from '@/store/utils';
import TaskPanel from '@/components/tasks/TaskPanel';
import moment from 'moment';
import urlState from "@/router/urlState";
import ComponentLabel from "@/components/ComponentLabel.vue";
import api from "@/api";
import ProductionSelector from "@/components/ProductionSelector.vue";
import DebouncedTextField from "@/components/DebouncedTextField.vue";

export default {
  name: "PrepTasks",
  components: {TaskPanel, ComponentLabel, ProductionSelector, DebouncedTextField},
  mixins: [urlState],

  mounted() {
    this.fetchRestrictions();
    // Load print timeout from localStorage
    const savedTimeout = localStorage.getItem('printTimeout');
    if (savedTimeout) {
      this.printTimeout = Number(savedTimeout);
      this.printTimeoutValue = Number(savedTimeout) / 1000; // Convert ms to seconds for display
    }

    this.syncToUrl({
      param: 'taskFilter', urlParam: 'status', initFromRoute: true,
      // parseCallback: (v) => v === 'true'
    });
    this.syncToUrl({
      param: 'showAllergy', urlParam: 'allergy', initFromRoute: true,
      parseCallback: (v) => v === 'true'
    });
    this.syncToUrl({
      param: 'denseView', urlParam: 'dense', initFromRoute: true,
      parseCallback: (v) => v === 'true'
    });
    this.syncToUrl({
      param: 'showGarnish', urlParam: 'garnish', initFromRoute: true,
      parseCallback: (v) => v === 'true'
    });
    this.syncToUrl({
      param: 'onlyGarnish', urlParam: 'garnish_only', initFromRoute: true,
      parseCallback: (v) => v === 'true'
    });
    this.syncToUrl({
      param: 'dates', urlParam: 'dates', initFromRoute: true,
      parseCallback: (v) => Array.isArray(v) ? v : [v]
    });
    this.syncToUrl({
      param: 'production', urlParam: 'production', initFromRoute: true,
      // parseCallback: (v) => Array.isArray(v) ? v : [v]
    });
    this.syncToUrl({
      param: 'search', urlParam: 'search', initFromRoute: true,
      // parseCallback: (v) => v === 'true'
    });
  },
  computed: {
    ...mapGetters(['getComponent', 'getMeal', 'getRestrictionText']),
    datesFormatted() {
      const format = 'dddd MMMM D';
      if (!this.dateFrom) {
        return '';
      } else if (this.dateFrom === this.dateTo) {
        return `${moment(this.dateFrom).format(format)}`;
      } else {
        return `${moment(this.dateFrom).format(format)} - ${moment(this.dateTo).format(format)}`
      }
    },
    dateFrom() {
      return [...this.dates].sort()[0];
    },
    dateTo() {
      return [...this.dates].sort().reverse()[0];
    },
    hasDates() {
      return this.dates && this.dates.length > 0;
    },
    filter() {
      const garnishVisible = this.alwaysShowGarnish || this.showGarnish;
      return garnishVisible ? '' : 'garnish';
    },
    tasksWithTimeEstimate() {
      return this.tasks.filter(t => t.source.timeEstimate)
    },
    totalTimeEstimate() {
      return this.tasksWithTimeEstimate.reduce((sum, t) => t.source.timeEstimate + sum, 0);
    }
  },

  methods: {
    ...mapActions(['fetchRestrictions']),
    /**
     * Allergy names for a task card: a short carries the allergies of its own line,
     * a component prep task carries the allergies forcing a substitute of the component.
     */
    /**
     * Allergy chip rows for a task card.
     * A short carries the allergies of its own line. A component prep task lists, per meal,
     * the allergies forcing a substitute; older tasks without per-meal data fall back to the
     * component-wide set.
     */
    /**
     * Rows of allergy chips for a task card. `names` (red) are allergies
     * detected in the component; `notInComponent` (grey) ride the allergy line
     * but are not in the component.
     */
    getTaskAllergyRows(task) {
      const source = task.source || {};
      const names = (restrictionIds) => (restrictionIds || []).map(this.getRestrictionText);
      const row = (key, label, detected, notInComponent = []) =>
        (detected.length || notInComponent.length) ? [{key, label, names: detected, notInComponent}] : [];
      if (source.isShort) {
        if (source.line === 'S') {
          return row('short', 'SUB for:', names(source.allergies), names(source.allergyLineAllergy));
        }
        if (source.line === 'AL') {
          return row('short', 'Allergy line:', [], names(source.allergies));
        }
        return [];
      }
      const mealRows = Object.entries(source.counts || {})
        .filter(([, count]) => count.activeAllergy && count.activeAllergy.length)
        .map(([mealId, count]) => ({
          key: mealId,
          label: `SUB ${count.key || mealId}:`,
          names: names(count.activeAllergy),
          notInComponent: names(count.allergyLineAllergy),
        }));
      if (mealRows.length) {
        return mealRows;
      }
      return row('all', 'SUB for:', names(source.activeAllergy));
    },
    formatAllergyNames(restrictionIds) {
      if (!this.showAllergyInfo) {
        return '';
      }
      const names = (restrictionIds || []).map(this.getRestrictionText);
      return names.length ? ` (${names.join(', ')})` : '';
    },
    getDuration(start, stop) {
      start = Date.parse(start);
      stop = Date.parse(stop);
      return stop - start;
    },
    formatTaskExecution(e) {
      const names = e.team_members.map(tm => tm.name).join(', ');
      const note = e.note ? ` | Note: ${e.note}` : '';
      const end = e.stop ? Date.parse(e.stop) : Date.now();
      const duration = formatMinutesSeconds(end - Date.parse(e.start));
      return `${names} worked for ${duration}${e.stop ? '' : ' so far'}.${note}`;
    },
    getDatesInRange,
    extractFoodId,
    getComponents(componentIds) {
      return componentIds.map(this.getComponent).sort((a, b) => a.name.localeCompare(b.name));
    },
    getTimerId(id) {
      return `I${id}`;
    },
    dateFormatted,
    formatDuration,
    formatMinutesSeconds,
    fetchData() {
      this.error = this.post = null
      this.loading = true
    },
    formatTaskAmount(task) {
      if (task.source) {
        const {source: {totalAmount, totalAmountAllergy = 0, cookedTotal, rawTotal}} = task;
        if (cookedTotal && cookedTotal === rawTotal) {
          return this.formatWeightWithUnits(cookedTotal);
        } else if (cookedTotal) {
          return `${this.formatWeightWithUnits(cookedTotal)}<br/>raw ${this.formatWeightWithUnits(rawTotal)}`;
        } else if (totalAmount) {
          if (totalAmount === totalAmountAllergy) {
            // allergy shorts were showing zero here
            return this.formatWeightWithUnits(totalAmount);
          }
          return this.formatWeightWithUnits(totalAmount - totalAmountAllergy);
        } else {
          return this.formatWeightWithUnits(0)
        }
      }
    },
    formatWeightWithUnits(amount) {
      const units = ['g', 'kg'];
      const decimalPlaces = [1, 2];
      return formatWeight(amount, units, decimalPlaces);
    },
    allowedDates(val) {
      const notAllowed = {
        0: true,
        // 2: true,
        // 4: true
      };

      return !notAllowed[moment(val).day()];
    },
    printStickers(task) {
      this.printTask = task;
      this.showStickers = true;
    },
    hasPrint() {
      return window.electronAPI && window.electronAPI.print
    },
    async doPrint() {
      try {
        if (window.electronAPI) {
          const result = await window.electronAPI.print({
            pageSize: {
              // height: 352 * 150,
              // width: 352 * 300,
              width: 352 * 150,
              height: 352 * 300,
            },
            // landscape: true,
            // scaleFactor: 85,
            margins: {
              marginType: 'none'
            },
            silent: true,
            // NOTE: this does not appear to work!  :/
            pageRanges: [{from: 0, to: 0}],
          });
          console.log('printed', result);
        }
      } catch (e) {
        console.error('electron print fail', e);
      }
    },
    formatDateRange(dateFrom, dateTo) {
      return `${moment(dateFrom).format('dd D')}-${moment(dateTo).format('dd D MMM')} `
    },
    closeStickers() {
      this.showStickers = false;
      const taskId = this.$route.query.sticker;
      const query = {...this.$route.query, task: taskId, sticker: null};
      this.$router.replace({query})
    },
    async printOnly(mealId, type) {
      this.isPrinting = true;
      console.log('selecting', this.selectedToPrint);
      const dPrintNoneStickerDisabled = 'd-print-none sticker-disabled';
      Object.keys(this.printTask.source.counts).forEach(mealId => {
        this.$set(this.stickerClass, `${mealId}-main`, dPrintNoneStickerDisabled);
        this.$set(this.stickerClass, `${mealId}-allergy`, dPrintNoneStickerDisabled);
        this.$set(this.stickerClass, `${mealId}-sub`, dPrintNoneStickerDisabled);
      });
      this.selectedToPrint = `${mealId}-${type}`;
      this.$set(this.stickerClass, this.selectedToPrint, '');
      setTimeout(async () => {
        await this.doPrint();
        setTimeout(() => {
          console.log('unselecting', this.selectedToPrint);
          this.selectedToPrint = false;
          this.stickerClass = {};
          this.isPrinting = false;
        }, this.printTimeout);
      }, this.printTimeout);
    },
    getMainRack(count) {
      if (count.key === 'CC' && count.rack === 'CC') {
        return '';
      }
      return count.rack ? `M${count.rack}` : ''
    },
    removeBatch(str) {
      return str.replace(/\[(.*)]/g, '');
    },
    subtractCount(countA = {}, countB = {}) {
      const result = {...countA};
      Object.keys(countB)
        .forEach(key => {
          result[key] = (countA[key] || 0) - countB[key]
        });
      return result;
    },
    taskFilter(t) {
      if (this.shortsOnly) {
        return t.source && t.source.isShort;
      }
      if (this.hideShorts) {
        return !(t.source && t.source.isShort)
      }
      return true;

    },
    saveUpdatedEstimate(task, updateTimeEstimate) {
      updateTimeEstimate = updateTimeEstimate && Number(updateTimeEstimate);
      if (this.team === 'chopping') {
        console.log('saveUpdatedEstimate', updateTimeEstimate, task)
        this.showUpdateEstimate = false;
        const {foodid, rawTotal} = task.source;
        const minutes_per_kg = Number(((updateTimeEstimate / rawTotal) * 1000).toFixed(2));
        console.log('saving new estimate', {foodid, updateTimeEstimate, rawTotal, minutes_per_kg});
        api.put('/v2/food/chopping-estimate/' + foodid, {minutes_per_kg});
        task.source.timeEstimate = updateTimeEstimate;
        this.$socket.emit('task:update', task);
        this.$socket.emit('tasks:cache:clear', {team: this.team});
      } else {
        alert('not supported yet')
      }

    },
    formatTimeDuration(durationInMinutes) {
      return `${moment.duration(durationInMinutes * 60 * 1000).asHours().toFixed(1)} hours`;
    },
    openTaskAnalysis(task) {
      const foodid = task.source.foodid;
      if (foodid) {
        let dates = this.dates;
        dates.sort();
        const first = dates[0];
        const last = dates[dates.length - 1];
        dates = [
          moment(first).subtract(4, 'days').format('YYYY-MM-DD'),
          moment(last).subtract(3, 'days').format('YYYY-MM-DD')
        ];

        this.$router.push({
          name: 'TaskAnalysis',
          params: {
            foodid: foodid
          },
          query: {
            dates,
            team: this.team
          }
        });
      }
    },
    currentTeamWorkingOnTask(task) {
      if (task.execution && task.execution.length > 0) {
        const lastExecution = task.execution[task.execution.length - 1];
        if (lastExecution.stop === null && lastExecution.team_members) {
          return lastExecution.team_members.map(m => m.name);
        }
      }
      return [];
    },
    saveTimeout() {
      this.printTimeout = this.printTimeoutValue * 1000; // Convert seconds to milliseconds
      localStorage.setItem('printTimeout', String(this.printTimeout));
      this.showTimeoutDialog = false;
    }
  },
  data() {
    return {
      // temporarily hidden on prep/chopping task cards; flip to true to restore allergy chips
      showAllergyInfo: false,
      dates: [],
      search: null,
      teamsSelected: null,
      showDatePicker: null,
      loading: null,
      datePickerDate: null,
      showGarnish: null,
      onlyGarnish: null,
      showStickers: null,
      printTask: null,
      mainCount: [],
      prepTaskWindow: null,
      stickerClass: {},
      isPrinting: null,
      denseView: true,
      showUpdateEstimate: null,
      updateTimeEstimate: null,
      tasks: [],
      production: null,
      printTimeout: 1000,
      printTimeoutValue: 1,
      showTimeoutDialog: false
    }
  },
  watch: {
    onlyGarnish(val) {
      if (val) {
        this.showGarnish = true;
        this.search = 'garnish';
      } else {
        this.showGarnish = false;
        this.search = this.search === 'garnish' ? '' : this.search;
      }
    },
    showStickers(v) {
      if (v) {
        this.prepTaskWindow = 1;
      } else {
        this.prepTaskWindow = 0;
      }
    }
  },
  props: {
    team: {type: String, required: true},
    teamFilter: {type: Array, default: null, required: false},
    showLateWarnings: {type: Boolean, default: false, required: false},
    sortFn: {type: Function, default: null, required: false},
    hideGarnish: {type: Boolean, default: false, required: false},
    alwaysShowGarnish: {type: Boolean, default: false, required: false},
    shortsOnly: {type: Boolean, default: false, required: false},
    hideShorts: {type: Boolean, default: false, required: false},
    lateWarningPriorityDeadlines: {type: Object, default: null, required: false},
    canSetEstimate: {type: Boolean, default: false, required: false},
    oneTaskAtATime: {type: Boolean, default: false, required: false}
  }
}
</script>

<style scoped>

</style>
